import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../redux/store';
import { setAuth } from '../../redux/authSlice';
import { useEffect, useState } from 'react';
import FormControlLabel from '@mui/material/FormControlLabel';
import { MaterialUISwitch } from '../util/MaterialUISwitch';
import DateTimeComponent from '../navComponents/DateTimeComponent';
import SidePanel from '../util/SidePanel';
import TemporaryDrawer from '../headerComponents/TemporaryDrawer';
import ProfileCard from '../modal/ProfileCard';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';
import SelectLanguage from '../headerComponents/SelectLanguage';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import LoginIcon from '@mui/icons-material/Login';
import Help from '../modal/Help';
import useIsMobile from '../util/useIsMobile';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';

interface HeaderProps {
  isDarkTheme: boolean,
  setIsDarkTheme: any,
  userInfo: string;
  refreshUserHeder: any;
  userRole: any;
  lng: any;
  setLng: any;
  userNotice: any;
  urlImage: any;
  baseUrl: string;
  is24HourFormat: boolean;
  timeZone: any;
  urlLogo: any
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  second_name: string;
  email: string;
  date_of_birth: Date;
  contract_start_date: Date;
  sex: string;
  mobile_phone: string;
  postcode: string;
  city: string;
  address: string;
  job_title: string;
  min_leave_entitlement: number;
  role: {
    id: number,
    title: string,
    color: string
  },
  company: {
    id: number,
    title: string
  },
  supervisorId: number;
  workingStatusId?: number | null;
  workingStatus?: { id: number; name: string; color: string; emoji: string } | null;
}

interface UserInfo {
  first_name: string;
  last_name: string;
  roleId: number;
}

const Header: React.FC<HeaderProps> = ({
  isDarkTheme,
  setIsDarkTheme,
  userRole,
  userInfo,
  refreshUserHeder,
  lng,
  setLng,
  userNotice,
  urlImage,
  baseUrl,
  is24HourFormat,
  timeZone,
  urlLogo }) => {
  const auth = useSelector((state: RootState) => state.auth.value);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const PAGE_TITLE_KEYS: Record<string, string> = {
    '/dashboard': 'Dashboard', '/timesheets': 'Timesheets',
    '/approve': 'Approve',     '/reports': 'Reports',
    '/docs': 'Docs',           '/crm': 'CRM',
    '/community': 'Community', '/equipment': 'Equipment',
    '/training': 'Training',   '/disk': 'Disk',
    '/booking': 'Booking',     '/finance': 'Finance',
    '/orders': 'Orders',       
    '/hr': 'HR',               '/toil': 'TOIL',
  };
  const pageTitleKey = PAGE_TITLE_KEYS[location.pathname] || 'Dashboard';
  const dateStr = new Date().toLocaleDateString('en-GB', {
    weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
  });
  const [show, setShow] = useState(false);
  // const [showSetting, setShowSetting] = useState(false); // OLD: merged into MyProfileCard
  const [allInfo, setAllInfo] = useState<UserInfo>({ first_name: '', last_name: '', roleId: 0 });
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [open, setOpen] = useState(false);
  const [help, setHelp] = useState(false);

  // Закрываем все панели при выходе из системы
  useEffect(() => {
    if (!auth) {
      setHelp(false);
      setShow(false);  // закрываем profile drawer — его backdrop блокировал логин-форму
      setOpen(false);  // закрываем notifications drawer
    }
  }, [auth]);
  const [companyName, setCompanyName] = useState();
  const [workingStatuses, setWorkingStatuses] = useState<{ id: number; name: string; color: string; emoji: string }[]>([])

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await axios.get('user');
        if (data) {
          const { data: companyNameData } = await axios.get(`/company/${data.companyId}`);
          setCompanyName(companyNameData.companyName)
        }
        dispatch(setAuth(true));
        setAllInfo(data)
        setSelectedUser(data)
        // load working statuses for the status selector
        try {
          const { data: statuses } = await axios.get('hr-working-status');
          setWorkingStatuses(Array.isArray(statuses) ? statuses : []);
        } catch {}
      } catch (error) {
        // auth errors handled by global interceptor — do not setAuth(false) here
      }
    };

    if (auth) {
      fetchData();
    }
  }, [auth, dispatch]);

  useEffect(() => {
    localStorage.setItem('darkTheme', JSON.stringify(isDarkTheme));
    document.body.classList.toggle('dark-theme', isDarkTheme);
  }, [isDarkTheme]);

  const logout = async () => {
    try {
      await axios.post('logout', {}, { withCredentials: true });
      axios.defaults.headers.common['Authorization'] = '';
      // Remove token from localStorage on logout
      localStorage.removeItem('authToken');
      dispatch(setAuth(false));
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  }

  const toggleTheme = () => {
    setIsDarkTheme(!isDarkTheme);
  };


  let links;

  if (auth) {
    const initials = `${allInfo.first_name?.[0] || ''}${allInfo.last_name?.[0] || ''}`;
    const notifCount = userNotice ? userNotice.length : 0;

    links = (
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 10 : 14 }}>

        {/* Время — скрыто на мобилке */}
        {!isMobile && <DateTimeComponent is24HourFormat={is24HourFormat} timeZone={timeZone} />}

        {/* Help — на мобилке только иконка */}
        <button
          type="button"
          className={`help-trigger-btn${help ? ' active' : ''}${isMobile ? ' m-hdr-icon' : ''}`}
          onClick={() => setHelp(h => !h)}
        >
          <div className="help-trigger-ic">?</div>
          {!isMobile && 'Help'}
        </button>

        {/* Уведомления — на мобилке иконка в хедере (в десктопе — в правом рельсе) */}
        {isMobile && (
          <button type="button" className="m-hdr-bell" onClick={() => setOpen(true)} aria-label={t('Notifications') as string}>
            <FontAwesomeIcon icon={faBell} />
            {notifCount > 0 && <span className="m-hdr-bell-dot">{notifCount}</span>}
          </button>
        )}

        {/* Notifications drawer */}
        <SidePanel open={open} onClose={toggleDrawer(false)}>
          <TemporaryDrawer userNotice={userNotice} refreshUserHeder={refreshUserHeder} onClose={toggleDrawer(false)} />
        </SidePanel>

        {/* Профиль: текст слева, аватар справа */}
        <div className="header-profile-area" onClick={() => setShow(true)} title={t('Profile')}>
          {!isMobile && <div className="header-profile-info">
            <div className="header-profile-name">{allInfo.first_name} {allInfo.last_name}</div>
            <div className="header-profile-role">{selectedUser?.job_title || ''}</div>
            {selectedUser?.workingStatus && (
              <div className="header-profile-status">
                <span className="header-status-dot" />
                {selectedUser.workingStatus.emoji} {selectedUser.workingStatus.name}
              </div>
            )}
          </div>}
          <div className="header-av-wrap">
            {urlImage
              ? <img className="header-av" src={urlImage} alt={initials} style={{ objectFit: 'cover' }} />
              : <div className="header-av">{initials}</div>
            }
            <div className="header-av-pulse" />
          </div>
        </div>

      </div>
    );
  } else {
    links =
      <Link to={'/login'}>
        <Button
          variant="outlined"
          color="warning"
          endIcon={<LoginIcon
            sx={{ color: '#cf6c02 !important' }} />}
          sx={{
            padding: '3px 20px',
            fontSize: 12,
            width: '100%'
          }}>
          {t('Login')}
        </Button>
      </Link>
  }

  return (
    <div className='header-box z-[1] h-[55px] relative w-full px-[20px] flex items-center justify-between'>

      {/* Лого — слева */}
      {auth && (
        <Link to="/" className='logo-a flex-shrink-0'>
          {urlLogo
            ? <img className="h-[36px] w-auto object-contain" src={urlLogo} alt="logo" />
            : <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-new)' }}>{companyName}</div>}
        </Link>
      )}

      {/* Spacer — название раздела перенесено в контент каждой страницы */}
      {auth && <div style={{ flex: 1 }} />}

      {/* Правая часть */}
      <div className="logo-info">
        <div className="user-info">
          {links}
        </div>
      </div>

      {/* Профиль drawer */}
      <SidePanel open={show} onClose={() => setShow(false)} width={760}>
        {selectedUser &&
          <ProfileCard
            isSelf={true}
            selectedUser={selectedUser}
            setShow={setShow}
            refreshUserHeder={refreshUserHeder}
            baseUrl={baseUrl}
          />}
      </SidePanel>

      {/* Help panel */}
      <Help open={help} onClose={() => setHelp(false)} />
    </div>
  )
}

export default Header